import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import { useDispatch } from 'react-redux'

import ENV_VARS from '@/config'
import structure from '@/structure'
import { breakpoints, colors } from '@/styles'
import useRole from '@/hooks/use-role'
import { Button } from '@/components/button'
import { PlayButtonIcon, WarningIconTriangle } from '@/components/icons'
import { show as showVideo } from '@/components/video-light-box'
import { VideoLinkPlayer } from '@/components/video-player'
import { showWarningModal } from '@/components/warning-modal'

import { SectionWrapper } from '../elements'

const { DASHBOARD_URL } = ENV_VARS

const OuterWrapper = styled.div`
  background-color: ${colors.blue96};
`

const Wrapper = styled(SectionWrapper)`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: space-between;
  max-width: none;
  padding: 0 !important;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    padding: 0;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 0;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    padding: 0;
  }
`

const TopSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  max-width: 1440px;
  margin: 0 auto;
  margin-top: ${structure.header.height}px;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: center;
    align-items: center;
    margin-top: ${structure.header.heightMobile}px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    justify-content: center;
    align-items: center;
  }
`

const PlayIconCustom = styled(PlayButtonIcon)`
  width: 22px;
  height: 22px;
  margin-right: 9px;
`

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 96px;
  flex: 1;
  z-index: 1;
  padding: 100px 0;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    margin: 0 48px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    flex-direction: column;
    padding: 100px 0;
    margin: 0 96px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
    padding: 48px 0;
    margin: 0 24px;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-width: 40%;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: unset;
    width: 100%;
    margin-bottom: 80px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    max-width: unset;
    width: 100%;
    margin-bottom: 60px;
  }
`

const Title = styled.h1`
  font-family: Inter, sans-serif;
  font-size: 60px;
  line-height: 73px;
  font-weight: 700;
  color: ${colors.grey08};
  margin: 0 0 24px 0;

  @media (max-width: ${breakpoints.v3Desktop}) {
    font-size: 48px;
    line-height: 52px;
  }

  @media (max-width: ${breakpoints.desktop}) {
    font-size: 32px;
    line-height: 40px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 60px;
    line-height: 72px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    font-size: 32px;
    line-height: 40px;
  }
`

const SubTitle = styled.p`
  font-family: Inter, sans-serif;
  font-size: 20px;
  line-height: 140%;
  color: ${colors.grey08};
  margin-bottom: 40px;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    font-size: 16px;
  }

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    font-size: 20px;
    line-height: 28px;
    margin: 0 0 12px 0;
  }
`

const buttonOneStyle = css`
  margin: 0;
  padding: 16px;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: center;
  min-width: unset;
  box-shadow: 0px 2px 4px rgba(19, 43, 62, 0.25),
    0px 6px 6px 2px rgba(19, 43, 62, 0.1);

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    align-self: center;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const buttonTwoStyle = css`
  margin: 0;
  padding: 16px;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  letter-spacing: 0em;
  text-align: center;
  min-width: unset;
  border: 1.5px solid ${colors.grey88};
  width: fit-content;
  margin: 0;
  box-shadow: 0px 2px 4px rgba(19, 43, 62, 0.25),
    0px 6px 6px 2px rgba(19, 43, 62, 0.1);

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    align-self: center;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    width: 100%;
  }
`

const Buttons = styled.div`
  display: flex;
  gap: 32px;
  width: 100%;

  @media (max-width: ${breakpoints.tabletLandscape}) {
    gap: 16px;
  }

  @media (max-width: ${breakpoints.phoneMax}) {
    flex-direction: column;
  }
`

const HeroVideoWrapper = styled.div`
  object-fit: contain;
  max-width: 54%;

  @media (max-width: ${breakpoints.ipadMiniMax}) {
    max-width: unset;
    width: 100%;
  }
`

const HeroVideo = styled.video`
  width: 100%;
  object-fit: contain;
`

const HeaderContent = () => {
  const { t } = useTranslation(['translation', 'landlords'])
  const dispatch = useDispatch()
  const logout = () => dispatch.auth.logout()
  const role = useRole()
  const router = useRouter()
  const isLandlord = role === 'landlord'
  const isTenant = role === 'tenant'

  const handleClick = () => {
    showVideo({
      videoLocation: 'https://www.youtube.com/embed/giA33lzlBUg',
      controls: true,
      autoPlay: true,
      PlayerComponent: VideoLinkPlayer
    })
  }

  const handleGetStarted = () => {
    if (isTenant) {
      showWarningModal({
        title: t(
          'r.landlord.view.logged_in_as_renter.title',
          "Oops! It looks like you're logged in as a renter."
        ),
        message: t(
          'r.landlord.view.logged_in_as_renter.text',
          'To access landlord-specific features, please log out and sign up for a landlord account or log in with your landlord account details.'
        ),
        icon: WarningIconTriangle,
        iconBgColor: '#FCF5D7',
        button: {
          text: t('common.log_out', 'Log Out'),
          onClick: logout
        }
      })
    } else {
      router.push(
        isLandlord ? `${DASHBOARD_URL}/listing/create` : '/post-a-listing'
      )
    }
  }

  return (
    <OuterWrapper>
      <Wrapper>
        <TopSection>
          <ContentWrapper>
            <TitleWrapper>
              <Title>{t('landlords:Find the right renters, faster')}</Title>
              <SubTitle>
                {t(
                  `landlords:From listing to signing, we've got you covered with time-saving, profit-maximizing solutions.`
                )}
              </SubTitle>
              <Buttons>
                <Button
                  onClick={handleGetStarted}
                  theme="primary"
                  as="a"
                  id="landlord-post-listing-hero-click"
                  type="button"
                  customStyle={buttonOneStyle}
                >
                  {t('landlords:Post a Listing')}
                </Button>
                <Button
                  onClick={handleClick}
                  id="landlord-watch-demo-hero-click"
                  customStyle={buttonTwoStyle}
                  theme="default"
                >
                  <PlayIconCustom />
                  {t('landlords:Watch Demo')}
                </Button>
              </Buttons>
            </TitleWrapper>
            <HeroVideoWrapper>
              <HeroVideo playsInline preload="auto" autoPlay muted loop>
                <source
                  src="/videos/landlord-hero-carousel.mp4"
                  type="video/mp4"
                />
              </HeroVideo>
            </HeroVideoWrapper>
          </ContentWrapper>
        </TopSection>
      </Wrapper>
    </OuterWrapper>
  )
}

export default HeaderContent
